import { createApi } from '@reduxjs/toolkit/query/react';
import { newBackendAxiosBaseQuery } from '../../utils/axiosBaseQuery';
import { serializeQueryArgsWithTenant } from '../tenantCacheKey';
import {
    CreateNonRoomBookingPayload,
    EditNonRoomBookingPayload,
} from '../../types/bookingCreationNonRoomRental';
import type { CustomerMailRefusal } from '../../utils/customerMailGuard';

// Dedicated slice for the booking write-path on the new (ECS) backend —
// Step 2 of the product-rental/storage/SUP self-service plan:
//   POST   /admin/order                    generalized create (storage/rental/sup)
//   POST   /admin/order/:orderId/cancel    soft-cancel (all use cases), ?saveMode=
//   GET    /admin/order/:orderId/cancellation-fee  what a cancel would charge (W1-6)
//   PATCH  /admin/order/:orderId           first-cut edit (dates/contact/comment)
//   PATCH  /admin/order/:orderId/payment   record the paid amount (mark-as-paid)
//   DELETE /admin/order/:orderId           hard delete (master-only)
// The booking read path (list, details) still lives on the legacy Lambda
// backend in `bookingApiSlice`.
//
// NOTE: the `/order/:orderId/payment` mark-as-paid endpoint is the documented
// backend contract from docs/plans/generalized-reservation-with-prepayment.md §7
// (money-state write, lead-dev hand-off). The UI ships against it (#802/#803
// pattern); until the endpoint lands the mutation surfaces its error state.

// Mark-as-paid payload — records the amount the customer has paid (offline /
// provider), from which the backend derives paid / partially_paid / overpaid.
export interface MarkNonRoomBookingPaidPayload {
    orderId: string;
    amountPaid: number;
    saveMode?: 'save_only' | 'save_and_send_email';
}

// Cancel payload — `saveMode` decides whether the customer is told. The mail
// used to go out on every cancel, which meant a booking taken by phone and
// confirmed verbally could reach the customer for the FIRST time as a
// cancellation notice. The operator now chooses in the confirm dialog.
export type CancelSaveMode = 'save_only' | 'save_and_send_email';

export interface CancelBookingPayload {
    bookingId: string;
    saveMode: CancelSaveMode;
    /**
     * M8 approval (#1809): cancel as "Decline" — only for a room reservation
     * still awaiting approval (409 otherwise). The customer gets the decline
     * mail instead of the cancellation mail; `reason` is internal and never
     * shown to them. Omitted = an ordinary cancel, as before.
     */
    decline?: { reason: string | null };
    /**
     * W1-6 (#3042): the staff choice when the booking's cancellation rule
     * charges a fee, and the amount the dialog showed — the server refuses a
     * cancel of such a booking without the choice, and one whose fee has
     * changed since (409 `fee_changed`). Omitted when no fee applies.
     */
    fee?: CancellationFeeChoice;
}

export interface CancellationFeeChoice {
    choice: 'charge' | 'waive';
    expectedFee: number;
}

/**
 * What a cancel of this booking would charge under the rule it was made under
 * (`GET /admin/order/:orderId/cancellation-fee`). Mirrors `CancellationFeeQuote`
 * in new-backend/admin `lib/booking/cancellationFee.ts`.
 */
export type CancellationFeeQuote =
    | { kind: 'not_applicable', reason: 'no_rule' | 'blocker' | 'covered' | 'paid' | 'not_accepted' }
    | { kind: 'free', freeUntil: string }
    | { kind: 'fee', percent: number, base: number, amount: number, freeUntil: string }
    | { kind: 'undetermined', reason: 'rule_unreadable' | 'price_unknown' };

/** The quote as the dialog holds it: with the live invoice the cancel will correct. */
export type CancellationFeeStep = CancellationFeeQuote & { liveInvoiceNumber: string | null };

// `mailSent` reports what the backend actually did, not what was asked for:
// a use case with no cancellation template (p2p/keys), a contact row without a
// usable address or a failed send all come back `false`, so the snackbar cannot
// claim a mail that never left. Same contract as `markNonRoomBookingPaid`.
export interface CancelBookingResponse {
    message: string;
    mailSent?: boolean;
    /**
     * W1-6 (D4 = A): a booking cancelled under its rule that was already
     * invoiced has its invoice corrected in the same step — a Storno plus the
     * fee-only invoice (`replace`), or the Storno alone (`cancel`). Absent
     * when there was nothing to correct.
     */
    invoiceCorrection?: InvoiceCorrectionAfterCancel;
}

export type InvoiceCorrectionAfterCancel =
    | {
        ok: true;
        mode: 'cancel' | 'replace';
        originalNumber: string;
        cancellationNumber: string;
        replacementNumber: string | null;
    }
    | { ok: false, mode: 'cancel' | 'replace', originalNumber: string, reason: string };

// The scope is always explicit. "Cancel everything" must never be what a
// request means by accident — a lost body would otherwise drop all forty
// occurrences instead of the five that were ticked, and mail the customer the
// list. (#2271: a body that was never parsed cost a release.)
export type CancelSeriesScope =
    | { scope: 'all' }
    | { orderIds: string[] };

export type CancelSeriesPayload = {
    seriesId: string;
    saveMode: CancelSaveMode;
} & CancelSeriesScope;

// `canceled` / `skipped` report what the request actually did: an occurrence
// cancelled individually beforehand is skipped, not failed, exactly as the
// browser loop used to tolerate a 409 and carry on.
export interface CancelSeriesResponse {
    seriesId: string;
    canceled: number;
    skipped: number;
    /** Occurrences the server could not cancel — reported, never inferred. */
    failed?: number;
    /** Requested ids that no longer exist, rather than silently dropped. */
    notFound?: number;
    mailSent?: boolean;
}
//
// Cross-slice cache invalidation (bookingList, bookingDetails on
// bookingApiSlice) is handled at the flow-hook call site by dispatching
// `bookingApiSlice.util.invalidateTags(...)` after a successful mutation.
// Same pattern as `bookingArchiveApiSlice` / `seriesBookingApiSlice`.
export const bookingWriteApiSlice = createApi({
    reducerPath: 'bookingWriteApiSlice',
    serializeQueryArgs: serializeQueryArgsWithTenant,
    baseQuery: newBackendAxiosBaseQuery(),
    endpoints: (builder) => ({
        // `mailBlocked`: the booking is saved, the customer mail refused (PO, 2026-10-30).
        createNonRoomBooking: builder.mutation<{ id: string, mailSent?: boolean, mailBlocked?: CustomerMailRefusal }, CreateNonRoomBookingPayload>({
            query: (data: CreateNonRoomBookingPayload) => {
                const { saveMode, ...payload } = data;
                const queryParam = saveMode ? `?saveMode=${saveMode}` : '';
                return {
                    url: `/order${queryParam}`,
                    method: 'POST',
                    data: payload,
                };
            },
        }),
        cancelBooking: builder.mutation<CancelBookingResponse, CancelBookingPayload>({
            query: ({ bookingId, saveMode, decline, fee }: CancelBookingPayload) => ({
                url: `/order/${bookingId}/cancel?saveMode=${saveMode}`,
                method: 'POST',
                // No body on an ordinary cancel without a fee: exactly the
                // request every caller sent before decline existed.
                ...(decline
                    ? { data: { decline: true, ...(decline.reason ? { reason: decline.reason } : {}) } }
                    : {}),
                ...(fee && !decline ? { data: { fee: fee.choice, expectedFee: fee.expectedFee } } : {}),
            }),
        }),
        // Read fresh on every cancel click (`forceRefetch` at the call site):
        // the fee depends on the hour it is asked.
        getCancellationFee: builder.query<CancellationFeeStep, string>({
            query: (orderId: string) => ({
                url: `/order/${orderId}/cancellation-fee`,
                method: 'GET',
            }),
            keepUnusedDataFor: 0,
        }),
        editNonRoomBooking: builder.mutation<{ id: string, mailBlocked?: CustomerMailRefusal }, EditNonRoomBookingPayload>({
            query: ({ orderId, saveMode, ...payload }: EditNonRoomBookingPayload) => ({
                url: `/order/${orderId}${saveMode ? `?saveMode=${saveMode}` : ''}`,
                method: 'PATCH',
                data: payload,
            }),
        }),
        // `mailSent` reports what the backend actually did, not what was asked
        // for — the payment can be recorded while the mail fails, and the toast
        // must not claim otherwise.
        markNonRoomBookingPaid: builder.mutation<
            { id: string, paymentStatus?: string, amountDue?: number, mailSent?: boolean, mailBlocked?: CustomerMailRefusal },
            MarkNonRoomBookingPaidPayload
        >({
            query: ({ orderId, ...payload }: MarkNonRoomBookingPaidPayload) => ({
                url: `/order/${orderId}/payment`,
                method: 'PATCH',
                data: payload,
            }),
        }),
        // One request cancels the selected occurrences of a series and sends
        // the customer ONE mail listing the dates that went — replacing a
        // browser loop over the per-booking endpoint, which sent one mail per
        // date. `orderIds` omitted cancels every occurrence.
        cancelSeriesBooking: builder.mutation<CancelSeriesResponse, CancelSeriesPayload>({
            query: ({ seriesId, saveMode, ...scope }: CancelSeriesPayload) => ({
                url: `/order/series/${seriesId}/cancel?saveMode=${saveMode}`,
                method: 'POST',
                // `scope` is `{ scope: 'all' }` or `{ orderIds: [...] }` — the
                // endpoint refuses a body that is neither rather than falling
                // back to cancelling the whole series.
                data: scope,
            }),
        }),
        deleteBooking: builder.mutation<{ message: string }, string>({
            query: (bookingId: string) => ({
                url: `/order/${bookingId}`,
                method: 'DELETE',
            }),
        }),
    }),
});

export const {
    useCreateNonRoomBookingMutation,
    useCancelBookingMutation,
    useCancelSeriesBookingMutation,
    useEditNonRoomBookingMutation,
    useMarkNonRoomBookingPaidMutation,
    useDeleteBookingMutation,
} = bookingWriteApiSlice;
